import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, GraduationCap, FileText, Megaphone, LogOut, KeyRound, UserCircle, Save, RefreshCw, Eye, FolderArchive, History, StickyNote, FileSignature, Video, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { useDocumentViewer } from '../components/DocumentViewer';
import CollegeManagement from './CollegeManagement';
import StudentManagement from './StudentManagement';
import LeadManagement from './LeadManagement';
import DocumentsArchive from './DocumentsArchive';
import ActivityLog from './ActivityLog';
import MasterSearch from '../components/MasterSearch';
import PersonalNotes from '../components/PersonalNotes';
import TeamChatWidget from '../components/TeamChatWidget';
import AgreementsLog from '../components/AgreementsLog';
import AppointmentsAdmin from '../components/AppointmentsAdmin';
import { fmtDate } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const ALL_NAV = [
  { key: 'recruiters', label: 'Recruiters', Icon: Users },
  { key: 'colleges', label: 'Colleges & Commissions', Icon: GraduationCap },
  { key: 'students', label: 'Student Applications', Icon: FileText },
  { key: 'leads', label: 'Lead Management', Icon: Megaphone },
];

// Manager-only modules (full visibility, no staff management).
const MANAGER_EXTRA_NAV = [
  { key: 'documents', label: 'Document Archive', Icon: FolderArchive },
  { key: 'activity', label: 'Activity Log', Icon: History },
];

const TITLES = {
  recruiters: { title: 'Recruiters', subtitle: 'Manage recruiter applications and partnerships.' },
  agreements: { title: 'Agreements', subtitle: 'Every recruiter agreement sent and signed via DocuSign.' },
  colleges: { title: 'Colleges & Commissions', subtitle: 'Partner colleges and commission rates.' },
  students: { title: 'Student Applications', subtitle: 'Review, assign and submit student applications.' },
  leads: { title: 'Lead Management', subtitle: 'Marketing leads and follow-ups.' },
  appointments: { title: 'Appointments', subtitle: 'Video-call bookings from students and recruiters.' },
  messages: { title: 'Messages', subtitle: 'Direct conversations with recruiters, students and your team.' },
  my_notes: { title: 'My Notes', subtitle: 'Private notes only you can see and delete.' },
  documents: { title: 'Document Archive', subtitle: 'Every file uploaded across the platform — recruiters, students and chat.' },
  activity: { title: 'Activity Log', subtitle: 'Append-only audit trail of every admin and staff action.' },
  profile: { title: 'My profile', subtitle: 'Your account details and access summary.' },
};

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [active, setActive] = useState('profile');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('tge-sidebar-collapsed') === '1'
  );
  const toggleSidebar = () => {
    setSidebarCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('tge-sidebar-collapsed', next ? '1' : '0'); } catch (_) { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    fetchMe();
  }, []);

  // Poll unread for sidebar badge
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/chat/unread`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (mounted) setUnreadTotal(d.total || 0);
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  async function fetchMe() {
    try {
      const res = await fetch(`${API}/api/staff/me`, { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        navigate('/login');
        return;
      }
      const d = await res.json();
      // Admins shouldn't land on /staff-dashboard — bounce them to their own dashboard.
      if (d.role === 'admin') {
        navigate('/admin-dashboard');
        return;
      }
      setMe(d);
      setProfileForm({ name: d.name || '' });
      // Default to the first module the user actually has access to, else profile.
      const modules = ['recruiters', 'colleges', 'students', 'leads'];
      const firstModule = modules.find((m) =>
        d.role === 'manager' || (d.permissions || []).some((p) => p === m || p.startsWith(`${m}.`))
      );
      setActive(firstModule || 'profile');
    } catch {
      setError('Failed to load profile');
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await fetch(`${API}/api/staff/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: profileForm.name }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      const d = await res.json();
      setMe(d);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleLogout() {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  }

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf8f3' }}>
        <div className="text-tge-muted">{error || 'Loading…'}</div>
      </div>
    );
  }

  const hasModule = (mod) =>
    me.role === 'manager' || me.permissions.some((p) => p === mod || p.startsWith(`${mod}.`));

  const visibleNav = [
    ...ALL_NAV.filter((n) => hasModule(n.key)),
    ...(hasModule('recruiters') ? [{ key: 'agreements', label: 'Agreements', Icon: FileSignature }] : []),
    { key: 'appointments', label: 'Appointments', Icon: Video },
    ...(me.role === 'manager' ? MANAGER_EXTRA_NAV : []),
  ];

  return (
    <div className="min-h-screen flex" style={{ background: '#faf8f3' }} data-testid="staff-dashboard">
      {/* Sidebar */}
      <aside
        className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-tge-navy/5 flex flex-col fixed inset-y-0 left-0 z-40 transition-[width] duration-200`}
        data-testid="staff-sidebar"
      >
        <div className={`${sidebarCollapsed ? 'px-2' : 'px-6'} py-6 border-b border-tge-navy/5 flex items-center gap-3 relative`}>
          <img src="https://customer-assets.emergentagent.com/job_student-pathfinder-21/artifacts/ujq9wzpi_True%20Global%20Logo.png" alt="True Global" className={`${sidebarCollapsed ? 'h-9 mx-auto' : 'h-11'} w-auto`} />
          {!sidebarCollapsed && (
            <div>
              <div className="font-serif-display text-base text-tge-navy leading-none">True Global</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-tge-gold mt-1 font-semibold">
                {me.role === 'manager' ? 'Manager Console' : 'Counsellor Console'}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="staff-sidebar-toggle"
            className="absolute -right-3 top-8 w-6 h-6 rounded-full bg-white border border-tge-navy/15 shadow-sm hover:border-tge-navy/40 flex items-center justify-center text-tge-navy transition"
          >
            {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1">
          <button onClick={() => setActive('profile')} data-testid="staff-nav-profile"
            title={sidebarCollapsed ? 'My profile' : undefined}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium transition-all ${active === 'profile' ? 'bg-tge-navy text-white shadow-sm' : 'text-tge-navy/80 hover:bg-tge-cream-soft hover:text-tge-navy'}`}>
            <UserCircle size={18} strokeWidth={1.7} />
            {!sidebarCollapsed && <span>My profile</span>}
            {!sidebarCollapsed && active === 'profile' && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-tge-gold" />}
          </button>

          <button onClick={() => setActive('my_notes')} data-testid="staff-nav-my-notes"
            title={sidebarCollapsed ? 'My Notes' : undefined}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium transition-all ${active === 'my_notes' ? 'bg-tge-navy text-white shadow-sm' : 'text-tge-navy/80 hover:bg-tge-cream-soft hover:text-tge-navy'}`}>
            <StickyNote size={18} strokeWidth={1.7} />
            {!sidebarCollapsed && <span>My Notes</span>}
            {!sidebarCollapsed && active === 'my_notes' && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-tge-gold" />}
          </button>

          {!sidebarCollapsed && visibleNav.length > 0 && <div className="text-[10px] uppercase tracking-wider text-tge-muted/70 mt-4 mb-1 px-3">Modules</div>}
          {visibleNav.map((it) => {
            const isActive = active === it.key;
            return (
              <button key={it.key} onClick={() => setActive(it.key)} data-testid={`staff-nav-${it.key}`}
                title={sidebarCollapsed ? it.label : undefined}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? 'bg-tge-navy text-white shadow-sm' : 'text-tge-navy/80 hover:bg-tge-cream-soft hover:text-tge-navy'}`}>
                <it.Icon size={18} strokeWidth={1.7} />
                {!sidebarCollapsed && <span>{it.label}</span>}
                {!sidebarCollapsed && isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-tge-gold" />}
              </button>
            );
          })}

          {!sidebarCollapsed && visibleNav.length === 0 && (
            <div className="mt-3 px-3 py-3 rounded-xl bg-amber-50 border border-amber-200 text-[12px] text-amber-800 leading-relaxed">
              You haven&apos;t been granted any module access yet. Ask your admin to assign permissions.
            </div>
          )}
        </nav>

        <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} py-4 border-t border-tge-navy/5 space-y-1`}>
          <button onClick={() => setPasswordOpen(true)} data-testid="staff-change-password-btn"
            title={sidebarCollapsed ? 'Change password' : undefined}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium text-tge-navy/70 hover:bg-tge-cream-soft hover:text-tge-navy transition`}>
            <KeyRound size={18} />
            {!sidebarCollapsed && <span>Change password</span>}
          </button>
          <button onClick={handleLogout} data-testid="staff-logout-btn"
            title={sidebarCollapsed ? 'Logout' : undefined}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition`}>
            <LogOut size={18} />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
          {!sidebarCollapsed && (
            <div className="mt-4 pt-4 border-t border-tge-navy/5 px-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-tge-navy to-blue-700 text-white flex items-center justify-center font-serif-display text-sm">
                  {me.name?.charAt(0)?.toUpperCase() || 'S'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-tge-navy truncate">{me.email}</div>
                  <div className="text-[10px] text-tge-muted capitalize">{me.role}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />

      <div className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} flex flex-col transition-[margin] duration-200`}>
        <header className="bg-white/80 backdrop-blur-md border-b border-tge-navy/5 px-8 py-5 sticky top-0 z-30">
          <div className="flex items-center gap-6">
            <motion.div key={active} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] text-tge-gold font-semibold">Staff</div>
              <h1 className="font-serif-display text-2xl text-tge-navy mt-0.5">{TITLES[active]?.title}</h1>
              <p className="text-sm text-tge-muted">{TITLES[active]?.subtitle}</p>
            </motion.div>
            <div className="flex-1 flex justify-end">
              <MasterSearch onNavigate={(tab) => hasModule(tab) && setActive(tab)} />
            </div>
          </div>
        </header>

        <main className="flex-1 px-8 py-7">
          {active === 'profile' && (
            <div className="max-w-xl space-y-6">
              <div className="bg-white rounded-2xl border border-tge-navy/5 p-6">
                <h3 className="font-serif-display text-lg text-tge-navy mb-4">Profile details</h3>
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div>
                    <Label>Full name</Label>
                    <Input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} required data-testid="staff-profile-name" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={me.email} readOnly className="bg-tge-cream-soft text-tge-navy font-medium" />
                    <p className="text-[11px] text-tge-muted mt-1">Email cannot be changed. Contact your admin.</p>
                  </div>
                  <Button type="submit" disabled={savingProfile} className="bg-tge-navy hover:bg-tge-navy-deep" data-testid="staff-profile-save">
                    <Save size={14} className="mr-1.5" /> {savingProfile ? 'Saving…' : 'Save changes'}
                  </Button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-tge-navy/5 p-6">
                <h3 className="font-serif-display text-lg text-tge-navy mb-2">Your access</h3>
                <p className="text-xs text-tge-muted mb-3">Modules you can use. To change this, ask your admin.</p>
                {me.permissions.length === 0 ? (
                  <p className="text-sm text-tge-muted italic">No modules assigned yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {me.permissions.map((p) => (
                      <span key={p} className="text-[11px] bg-tge-navy text-white px-3 py-1 rounded-full capitalize">{p}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {active === 'recruiters' && hasModule('recruiters') && <StaffRecruitersView />}
          {active === 'agreements' && hasModule('recruiters') && <AgreementsLog />}
          {active === 'colleges' && hasModule('colleges') && <CollegeManagement />}
          {active === 'students' && hasModule('students') && <StudentManagement />}
          {active === 'leads' && hasModule('leads') && <LeadManagement />}
          {active === 'appointments' && <AppointmentsAdmin />}
          {active === 'documents' && me.role === 'manager' && <DocumentsArchive />}
          {active === 'activity' && me.role === 'manager' && <ActivityLog />}
          {active === 'my_notes' && <PersonalNotes />}
        </main>
      </div>
      <TeamChatWidget currentUserId={me.id} />
    </div>
  );
}


const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-800',
  agreement_sent: 'bg-sky-100 text-sky-800',
  agreement_signed: 'bg-violet-100 text-violet-800',
  signed: 'bg-violet-100 text-violet-800',
  active: 'bg-emerald-100 text-emerald-800',
};

const STATUS_LABEL = {
  pending: 'Pending',
  agreement_sent: 'Agreement Sent',
  agreement_signed: 'Agreement Signed',
  signed: 'Agreement Signed',
  active: 'Active',
};

function StaffRecruitersView() {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const viewer = useDocumentViewer();

  useEffect(() => {
    fetchRecruiters();
  }, []);

  async function fetchRecruiters() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/recruiters`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setRecruiters(d.recruiters || []);
      }
    } catch {/* ignore */} finally { setLoading(false); }
  }

  const counts = {
    total: recruiters.length,
    pending: recruiters.filter((r) => r.status === 'pending').length,
    sent: recruiters.filter((r) => r.status === 'agreement_sent').length,
    signed: recruiters.filter((r) => r.status === 'agreement_signed' || r.status === 'signed').length,
    active: recruiters.filter((r) => r.status === 'active').length,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { l: 'Total', v: counts.total, c: 'text-tge-navy' },
          { l: 'Pending', v: counts.pending, c: 'text-amber-600' },
          { l: 'Agreement Sent', v: counts.sent, c: 'text-sky-600' },
          { l: 'Signed', v: counts.signed, c: 'text-violet-600' },
          { l: 'Active', v: counts.active, c: 'text-emerald-600' },
        ].map((s) => (
          <div key={s.l} className="bg-white rounded-2xl p-5 border border-tge-navy/5">
            <div className="text-[11px] uppercase tracking-wider text-tge-muted">{s.l}</div>
            <div className={`font-serif-display text-3xl mt-2 ${s.c}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
        <div className="px-6 py-5 border-b border-tge-navy/5 flex items-center justify-between">
          <h3 className="font-serif-display text-xl text-tge-navy">Recruiter applications</h3>
          <button onClick={fetchRecruiters} className="inline-flex items-center gap-1.5 text-xs text-tge-muted hover:text-tge-navy transition px-3 py-1.5 rounded-full border border-tge-navy/10 hover:border-tge-navy/30 hover:bg-tge-cream-soft">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="text-center py-12 text-tge-muted text-sm">Loading…</div>
        ) : recruiters.length === 0 ? (
          <div className="text-center py-12 text-tge-muted text-sm">No recruiters visible to you yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-tge-cream-soft">
                <tr>
                  {['Recruiter', 'Company', 'Status', 'Documents', 'Sent / Activated'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-semibold text-tge-muted uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-tge-cream">
                {recruiters.map((r) => (
                  <tr key={r.recruiter_id} className="hover:bg-tge-cream-soft/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-tge-gold/20 text-tge-gold font-serif-display flex items-center justify-center text-sm">
                          {r.company_name?.charAt(0) || 'R'}
                        </div>
                        <div>
                          <div className="font-medium text-tge-navy text-sm">{r.contact_name || '—'}</div>
                          <div className="text-xs text-tge-muted">{r.contact_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-tge-navy">{r.company_name}</td>
                    <td className="px-6 py-4">
                      <Badge className={`${STATUS_COLOR[r.status] || 'bg-gray-100 text-gray-800'} border-0`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1"
                          disabled={!r.business_license_url}
                          onClick={() => viewer.open({
                            url: `${API}/api/admin/recruiters/${r.recruiter_id}/documents/business_license`,
                            label: 'Business License',
                            ownerName: r.company_name,
                          })}
                        >
                          <Eye size={11} /> License
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1"
                          disabled={!r.id_proof_url}
                          onClick={() => viewer.open({
                            url: `${API}/api/admin/recruiters/${r.recruiter_id}/documents/id_proof`,
                            label: 'ID Proof',
                            ownerName: r.company_name,
                          })}
                        >
                          <Eye size={11} /> ID
                        </Button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs text-tge-muted">
                      {r.activated_at && <>Activated {fmtDate(r.activated_at)}</>}
                      {!r.activated_at && r.agreement_sent_at && <>Agreement sent {fmtDate(r.agreement_sent_at)}</>}
                      {!r.activated_at && !r.agreement_sent_at && '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {viewer.element}
    </div>
  );
}
